import React, { useEffect, useMemo } from 'react';
import { InputAdornment, styled } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import GroupsIcon from '@mui/icons-material/Groups';
import CancelRounded from '@mui/icons-material/CancelRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextField from '@mui/material/TextField';
import Box from '@mui/system/Box';
import './ConferenceFilter.scss';
import { useFetchConferences } from '../../hooks/useFetch';
import { useFilterContext, useFilterDispatchContext } from '../../contexts/FilterContext';
import { ConferenceInfo } from '../../../api/types';
import SearchDebounce from '../../utils/common/search';

interface IProps {
  label: string;
  actionType: string;
  actionObject: string;
  apiUrl: string;
  includeSpecialValues?: boolean;
}

const StyledAutocomplete = styled(Autocomplete)((props: { textcolor: string, weight: number }) => ({
  '& .MuiInputBase-root': {
    paddingLeft: '35px !important',
    '& .MuiAutocomplete-input': {
      color: props.textcolor,
      fontWeight: props.weight,
    },
    '& .MuiAutocomplete-endAdornment': {    
      '& .MuiAutocomplete-clearIndicator': {
        '& .cancel-icon': {
          color: props.textcolor,
          visibility: 'visible',
        }
      }
    },
  },
}));

const STATIC_CONFERENCE_OPTIONS = [
  {
    conference_id: 0,
    short_name: 'Conference Opponent (all games)',
    full_name: 'Conference Opponent (all games)',
    specialValue: 'conference',
  },
  {
    conference_id: 0,
    short_name: 'Non-Conference Opponent (all games)',
    full_name: 'Non-Conference Opponent (all games)',
    specialValue: 'nonconference',
  },
];

function ConferenceFilter({
  label,
  actionType,
  actionObject,
  apiUrl,
  includeSpecialValues = false,
}: IProps) {
  const filterContext: any = useFilterContext();
  const filterDispatchContext: any = useFilterDispatchContext();

  const { data: conferenceData } = useFetchConferences('', apiUrl);
  const conferenceOptions = useMemo(() => {
    const uniqueOptions = new Map<number, ConferenceInfo>();
    (conferenceData?.results ?? []).forEach((option: ConferenceInfo) => {
      if (!uniqueOptions.has(option.conference_id)) {
        uniqueOptions.set(option.conference_id, option);
      }
    });
    const realOptions = [...uniqueOptions.values()];
    return includeSpecialValues ? [...STATIC_CONFERENCE_OPTIONS, ...realOptions] : realOptions;
  }, [conferenceData, includeSpecialValues]);

  const searchDebounce: SearchDebounce = useMemo(() => new SearchDebounce(), [ ]);

  // Clean up this debounce when it is unmounted
  useEffect(() => () => searchDebounce.clear(), [searchDebounce]);

  const handleChange = (_event: any, newValue: ConferenceInfo | null) => {
    filterDispatchContext({
      type: actionType,
      [actionObject]: newValue,
      searchEnabled: true,
    });
  };

  const filterOptions = (options: unknown[], { inputValue }: { inputValue: string }) => {
    const filteredOptions = (options as any[]).filter((option: any) => {
      if (option.specialValue) {
        return true;
      }
      return option.full_name?.toLowerCase().includes(inputValue.toLowerCase());
    });
    return [...new Set(filteredOptions)];
  };

  const textColor = filterContext[actionObject] &&
    (filterContext[actionObject].conference_id || filterContext[actionObject].specialValue)
      ? '#175CD3' : '#101828';
  const fontweight = filterContext[actionObject] &&
    (filterContext[actionObject].conference_id || filterContext[actionObject].specialValue)
      ? 500 : 400;

  return (
    <Box>
      <StyledAutocomplete
        data-testid="elias-conference-filter"
        options={conferenceOptions}
        value={filterContext[actionObject]}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option: any, value: any) =>
          option.specialValue
            ? option.specialValue === value?.specialValue
            : !value?.specialValue && option.conference_id === value?.conference_id
        }
        noOptionsText="No matches found."
        getOptionLabel={(option: any) => option.full_name ?? option.short_name ?? ''}
        onChange={(event: any, value: any) => handleChange(event, value)}
        renderInput={(params) => (
          <TextField {...params} placeholder={label} InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <GroupsIcon className="groups-icon" />
              </InputAdornment>
            ),
          }}/>
        )}
        renderOption={(props, option: any) => (
          <li {...props} key={option.conference_id ?? option.specialValue}>
            {option.full_name}
          </li>
        )}
        popupIcon={
          <ExpandMoreIcon />
        }
        forcePopupIcon={!filterContext[actionObject]?.id}
        clearIcon={
          <CancelRounded className='cancel-icon' />
        }
        textcolor={textColor}
        weight={fontweight}
      />
    </Box>
  );
}

export default ConferenceFilter;
